import React, { useState } from 'react';
import classNames from 'clsx';
import { FormControl, TextField } from '@material-ui/core';

import { UtilService } from 'app/services/util.service';
import { clearAnonymization } from 'app/shared/helpers/clearAnonymization';
import { validateIdCardNumber } from 'app/validators';
import { ControlContainer } from 'app/components/ControlContainer';
import { DoneAdornment } from 'app/components/DoneAdornment';
import { IdCardNumberControlProps } from './IdCardNumberControl.interfaces';
import { useStyles } from './IdCardNumberControl.styles';

export default function IdCardNumberControl({
  input,
  input: { value: inputValue },
  meta,
  meta: { touched, valid },
  label,
  tooltip,
  subLabel,
  helperText,
  disabled,
  width,
  isQuestionValid,
  withMarginIfValid,
  onControlBlur,
  onValueChange,
  onClick,
}: IdCardNumberControlProps) {
  const ID_CARD_NUMBER_MAX_LENGTH = 9;
  const [focused, setFocused] = useState<boolean>(false);
  const classes = useStyles();

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    handleFocus(e.target.value);
    setFocused(true);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = event.target;
    value = value.replace(/\s+/g, '');
    if (value.length > ID_CARD_NUMBER_MAX_LENGTH) {
      return;
    }

    if (!/^[a-z]{0,3}[0-9]{0,6}$/i.test(value)) {
      return;
    }

    input.onChange(value);
    if (onValueChange) {
      onValueChange(value, false, false);
    }
  };

  const handleFocus = (value: string) => {
    const clearedValue = clearAnonymization(value);

    if (clearedValue !== value) {
      input.onChange(clearedValue);
      if (onValueChange) {
        onValueChange(clearedValue, false, false);
      }
    }
  };

  const handleBlur = () => {
    const formattedValue = inputValue.toUpperCase();
    const shouldPatch =
      validateIdCardNumber(formattedValue) || formattedValue === '';

    setFocused(false);
    input.onBlur(formattedValue);
    if (onControlBlur) {
      setTimeout(() => {
        onControlBlur(shouldPatch);
      }, 0);
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <ControlContainer
      inputId={input.name}
      tooltip={tooltip}
      subLabel={subLabel}
      helperText={helperText}
      isQuestionValid={isQuestionValid}
      meta={meta}
      floating
      floated={focused || UtilService.hasValue(inputValue)}
      focused={focused}
      width={width}
    >
      <FormControl fullWidth>
        <TextField
          {...input}
          id={input.name}
          disabled={disabled}
          onChange={handleChange}
          onBlur={handleBlur}
          onClick={handleClick}
          onFocus={onFocus}
          label={label}
          InputProps={{
            startAdornment: valid &&
              !focused &&
              UtilService.hasValue(inputValue) && <DoneAdornment />,
          }}
          error={touched && !valid}
          variant={'outlined'}
          margin={'dense'}
          InputLabelProps={{
            shrink: UtilService.hasValue(inputValue) || focused,
          }}
          className={classNames({
            [classes.withMarginBottom]:
              withMarginIfValid && (valid || !touched),
          })}
        />
      </FormControl>
    </ControlContainer>
  );
}
